// @ts-nocheck
/**
 * Article HTML preparation for the Copilot (beta) citation slide-out (ACOP-93).
 *
 * Deliberately free of React so the rules can be unit-tested directly — the same reasoning as
 * utils/nav.ts. Everything here exists to serve one hard requirement: **no click anywhere in the
 * slide-out may leave Copilot.** An article link the proxy did not vouch for becomes an in-panel
 * dead page naming the missing article, which is what makes broken links visible during testing
 * instead of silently escaping to another site.
 *
 * Three jobs, in order:
 *
 *   1. SANITISE — third layer, after AVSOS at ingest and avsOsCitation.ts server-side.
 *   2. SCOPE THE CSS — see scopeArticleCss. Articles ship their own stylesheet and the KB table of
 *      contents ships `*` and `body` rules; injected unscoped they would restyle the whole app.
 *   3. HARDEN THE LINKS — fail CLOSED. An anchor carrying no proxy marker is one the proxy did not
 *      resolve, so it is turned into a dead link rather than left to navigate.
 */

/** Marker the citation proxy puts on a link it resolved to another in-panel article. */
export const CITATION_ATTR = "data-avs-citation";

/** Marker for a cross-article link that cannot be resolved to an article. */
export const DEAD_LINK_ATTR = "data-avs-kb-missing";

/**
 * Marker the server (avsOsArticleHelper.ts) puts on an external link it deliberately exempted from
 * the dead-link treatment — e.g. the Home page's marketing CTAs (ACOP-120, `HOME_EXTERNAL_ALLOWLIST`).
 * Narrow and server-decided on purpose: the client only ever honours a marker the server already
 * vouched for, never re-derives its own allowlist.
 */
export const EXTERNAL_OK_ATTR = "data-avs-external-ok";

/** Class the dead-link stylesheet hangs off. Also set by AVSOS when it emits the marker itself. */
export const DEAD_LINK_CLASS = "kb-dead-link";

/** The panel container every article rule gets scoped under. Defined in index.css. */
export const ARTICLE_SCOPE = ".c2-panel-content";

/**
 * The full-page article container on Knowledge Base (beta), the other place an article renders
 * (ACOP-100). Defined in KnowledgeBaseTwo.css.
 *
 * A second scope rather than a shared one because the two containers can be on screen at once: the
 * slide-out opens OVER the KB page, and a stylesheet scoped to the panel must not restyle the page
 * behind it. That is why scopeArticleCss takes the scope as an argument rather than closing over a
 * constant — the scope is a property of where the article is being rendered, not of this module.
 */
export const KB_PAGE_SCOPE = ".kb2-article";

/**
 * The Home (beta) container, the third place knowledge-base content renders (ACOP-101).
 *
 * A third scope for the same reason there is a second one: the citation slide-out opens OVER Home,
 * so Home's stylesheet must not restyle the panel in front of it, and the panel's must not restyle
 * Home behind it. Defined in DashboardHome.css.
 *
 * Unlike an article, the Home payload's own CSS is already self-scoped under `.copilot-home` by its
 * author, so scoping yields `.copilot-home-page .copilot-home …`. It still goes through
 * scopeArticleCss: the file being well-behaved today is not a reason to trust the next revision of
 * it, and the whole point of the feature is that its authors can change it without a release.
 */
export const HOME_PAGE_SCOPE = ".copilot-home-page";

/**
 * Every element that navigates on click.
 *
 * `area` matters as much as `a`. An <area> inside an image map is a link with a click target, and
 * DOMPurify permits map/area/usemap/shape/coords by default — so hardening that keys only on "a"
 * leaves it live, and one click on the image takes the whole browser off Copilot. An SVG <a> is
 * already covered, since it is an `a` element. Keep this in step with the click interception in
 * CitationTwo.tsx and with the server-side rewrite in avsOsArticleHelper.ts.
 */
export const LINK_ELEMENTS = "a, area";

/**
 * Schemes that are not page navigation and are left alone. A mailto: hands off to a mail client
 * without moving the app, so neutralising it would remove a working affordance for no gain.
 */
const NON_NAVIGATING_SCHEME = /^(mailto:|tel:|sms:)/i;

/**
 * Tags dropped on top of DOMPurify's defaults.
 *
 * `form` and `base` are the two that matter: DOMPurify permits <form> by default, and a <base>
 * would re-point every relative URL in the panel at another origin. avsOsCitation.ts already
 * strips forms server-side; this keeps the client honest on its own. The document-head tags are
 * cosmetic — an article fragment that carries <title> renders it as stray text in the panel.
 */
const FORBID_TAGS = ["form", "base", "title", "meta", "link", "noscript"];

/**
 * Attributes kept beyond DOMPurify's defaults. `target` needs listing explicitly — DOMPurify
 * strips it by default (confirmed by test; `rel` is unaffected and needs no entry). Every other
 * anchor path in this module removes `target` again immediately after sanitising, so this only
 * matters for the one shape meant to keep it: an `EXTERNAL_OK_ATTR`-marked link (ACOP-120).
 */
const ADD_ATTR = ["style", CITATION_ATTR, DEAD_LINK_ATTR, "target"];

/** Tags kept beyond DOMPurify's defaults. <style> carries the article's vendored CSS. */
const ADD_TAGS = ["style"];

/**
 * An anchor that was pointing out of the panel, recorded so the caller can assert there were none
 * and so a test can name what leaked.
 */
export type DeadLink = {
  /** The KB urlname where one could be derived, else a readable stand-in. */
  urlname: string;
  /** The link's visible text, which is what the reader clicked. */
  label: string;
  /** The href as it arrived, kept for diagnosis. Never rendered as a live link. */
  href: string;
};

export type PreparedArticle = {
  /** Sanitised, CSS-scoped, link-hardened HTML, safe to inject into the panel. */
  html: string;
  /** Anchors converted to dead links because nothing vouched for them. */
  neutralised: DeadLink[];
  /**
   * `src` values that arrived relative.
   *
   * AVSOS guarantees absolute public URLs at ingest, and the whole corpus honours it — 4,130 of
   * 4,130 `[src]` values across 268 staging articles are absolute or data:, none relative. (Mostly
   * kb.inscaletech.com, but also a handful on s3.amazonaws.com and googleusercontent.com, so this
   * does not check the host.) A relative one would be resolved against the AVSOS host by the proxy
   * and 404, so this exists to prove the guarantee rather than trust it (ACOP-93).
   */
  relativeSrcs: string[];
};

/**
 * Pull the KB urlname out of a citation path or article URL.
 *
 * AVSOS serves articles at `/api/public/knowledge/<urlname>/html`. Some hrefs in the corpus are
 * malformed — `/api/public/knowledge/part-masters#create/html` puts the fragment mid-path — so the
 * urlname is read from the path segment after `knowledge/` and the fragment is ignored.
 */
export function kbUrlnameFromHref(href: string): string {
  if (!href) return "";
  const withoutFragment = href.split("#")[0].split("?")[0];
  const match = withoutFragment.match(/\/knowledge\/([^/]+)/);
  if (match) return decodeURIComponent(match[1]);
  // Not a knowledge URL. Fall back to the last meaningful path segment.
  const segments = withoutFragment.split("/").filter(Boolean);
  const last = segments[segments.length - 1] || "";
  return last === "html" ? segments[segments.length - 2] || "" : last;
}

/** What to show a reader for a link that goes nowhere. */
export function describeDeadLink(href: string, text: string): DeadLink {
  const urlname = kbUrlnameFromHref(href);
  const label = (text || "").trim() || urlname || "this article";
  return { urlname, label, href: href || "" };
}

/**
 * Prefix one CSS selector so it can only match inside the panel.
 *
 * `body`, `html` and `:root` are rewritten to the scope rather than nested under it — a `body`
 * rule is describing the page canvas, and the panel content is that canvas here.
 */
function scopeSelector(selector: string, scope: string): string {
  const trimmed = selector.trim();
  if (!trimmed) return "";
  // Already scoped — leave idempotent so re-preparing HTML is safe.
  if (trimmed === scope || trimmed.startsWith(scope + " ")) return trimmed;
  const canvas = trimmed.match(/^(html|body|:root)\b(.*)$/i);
  if (canvas) return (scope + canvas[2]).trim();
  return scope + " " + trimmed;
}

/** Index just past a quoted string starting at `open`, honouring backslash escapes. */
function skipString(css: string, open: number): number {
  const quote = css[open];
  let i = open + 1;
  while (i < css.length) {
    if (css[i] === "\\") { i += 2; continue; }
    if (css[i] === quote) return i + 1;
    i += 1;
  }
  return css.length;
}

/** Index just past a comment starting at `open`, or `open` if there is no comment there. */
function skipComment(css: string, open: number): number {
  if (!css.startsWith("/*", open)) return open;
  const close = css.indexOf("*/", open + 2);
  return close === -1 ? css.length : close + 2;
}

/**
 * If an unquoted CSS `url(...)` argument starts at `i`, the index just past its closing `)`.
 * Returns `i` unchanged when there is no `url(` there, or when the argument is itself quoted
 * (`url("...")` / `url('...')`) — that shape is already handled correctly by the ordinary
 * quote-skipping below, since the opening quote is the very next character.
 *
 * Exists because a hand-written icon background is routinely an UNQUOTED data URI carrying raw,
 * unescaped SVG markup — `url(data:image/svg+xml,<svg xmlns='...'>...</svg>)` — and that markup's
 * own quote characters are not CSS string delimiters. Without this, an odd number of a given quote
 * character inside such a value (one stray apostrophe is enough) makes the quote-skipping below
 * hunt for a closing partner that never comes, consuming the rest of the stylesheet as "inside a
 * string" and leaving every rule after it unscoped. Confirmed against a real incident (2026-09-21,
 * AvSight Copilot home page) where this exact shape took an entire stylesheet unstyled.
 */
function skipUnquotedUrl(css: string, i: number): number {
  if (css[i] !== "u" && css[i] !== "U") return i;
  if (!/^url\(/i.test(css.slice(i, i + 4))) return i;
  if (/[\w-]/.test(css[i - 1] || "")) return i; // mid-identifier, e.g. a custom property `--my-url(`
  let j = i + 4;
  while (j < css.length && /\s/.test(css[j])) j += 1;
  if (css[j] === '"' || css[j] === "'") return i; // quoted — the string-skipper below handles it
  while (j < css.length && css[j] !== ")") j += 1;
  return j < css.length ? j + 1 : j;
}

/**
 * Remove every CSS comment from a stylesheet, leaving strings and unquoted `url(...)` arguments exactly
 * as written (so a `/*` inside either is NOT mistaken for a comment).
 *
 * Why this exists (ACOP-148): DOMPurify discards a WHOLE `<style>` block when its text contains a `<`
 * followed by a word character — even inside a CSS comment. The shared article stylesheet carries one
 * ("most phones rotated landscape have <430px of height"), so every git-sourced article lost its entire
 * stylesheet in the app: no navy header behind the white logo, no callout boxes. Comments are dead text to
 * a browser, so removing them before sanitising costs nothing and cannot be re-broken by a future
 * comment. A comment is replaced by nothing, never by a space: `.a/**\/.b` must not become the
 * descendant selector `.a .b`.
 */
export function stripCssComments(css: string): string {
  if (!css || css.indexOf("/*") === -1) return css || "";
  let out = "";
  let i = 0;
  while (i < css.length) {
    if (css.startsWith("/*", i)) { i = skipComment(css, i); continue; }
    const afterUrl = skipUnquotedUrl(css, i);
    if (afterUrl !== i) { out += css.slice(i, afterUrl); i = afterUrl; continue; }
    const c = css[i];
    if (c === '"' || c === "'") {
      const end = skipString(css, i);
      out += css.slice(i, end);
      i = end;
      continue;
    }
    out += c;
    i += 1;
  }
  return out;
}

/** Strip the comments out of every `<style>` block in an HTML fragment, before it is sanitised. */
function stripStyleComments(html: string): string {
  return html.replace(/(<style\b[^>]*>)([\s\S]*?)(<\/style>)/gi, (_m, open, css, close) => open + stripCssComments(css) + close);
}

/**
 * Find the first `{` or `;` at nesting depth zero, ignoring anything inside a comment, a string,
 * or an unquoted `url(...)` argument. Returns the index and which one it found, so a rule and a
 * bare statement can be told apart.
 */
function nextDelimiter(css: string, from: number): { at: number; kind: "block" | "statement" | "end" } {
  let i = from;
  while (i < css.length) {
    const c = css[i];
    if (c === "/" && css[i + 1] === "*") { i = skipComment(css, i); continue; }
    const afterUrl = skipUnquotedUrl(css, i);
    if (afterUrl !== i) { i = afterUrl; continue; }
    if (c === '"' || c === "'") { i = skipString(css, i); continue; }
    if (c === "{") return { at: i, kind: "block" };
    if (c === ";") return { at: i, kind: "statement" };
    i += 1;
  }
  return { at: css.length, kind: "end" };
}

/** Index of the `}` closing the block that opens at `open`. */
function matchingBrace(css: string, open: number): number {
  let depth = 0;
  let i = open;
  while (i < css.length) {
    const c = css[i];
    if (c === "/" && css[i + 1] === "*") { i = skipComment(css, i); continue; }
    const afterUrl = skipUnquotedUrl(css, i);
    if (afterUrl !== i) { i = afterUrl; continue; }
    if (c === '"' || c === "'") { i = skipString(css, i); continue; }
    if (c === "{") depth += 1;
    else if (c === "}") {
      depth -= 1;
      if (depth === 0) return i;
    }
    i += 1;
  }
  return css.length;
}

/**
 * What to do with an at-statement that has no block — `@import`, `@charset`, `@namespace`.
 *
 * `@import` is dropped. Article CSS is injected into the page, so honouring an @import would have
 * the panel fetch a stylesheet from wherever the article's author pointed it. Nothing in the AVS OS
 * corpus uses one, and there is no version of "the article asked us to load remote CSS" that the
 * panel should agree to. The rest are inert once inlined and are kept so the stylesheet still reads
 * the way its author wrote it.
 */
function keepStatement(statement: string): string {
  return /^\s*@import\b/i.test(statement) ? "" : statement;
}

/**
 * Confine a stylesheet to the citation panel.
 *
 * The KB table of contents ships `* { box-sizing }`, `*::before`, `*::after` and `body { ... }`.
 * Injected as-is those rules apply to the entire single-page app for as long as the panel is open,
 * which restyles pages that have nothing to do with the beta. Articles themselves are class-scoped
 * today, but that is the content author's choice and not something to depend on.
 *
 * A structural walk rather than a regex, because the failure mode is silent: it has to tell a rule
 * (`selector { ... }`) from a statement (`@import ...;`), or the statement is swallowed into the
 * next rule's prelude, the combined prelude starts with `@`, and the following `body` rule ships
 * unscoped. Nested at-rules recurse — @media/@supports/@container/@layer/@scope — while
 * @keyframes and @font-face are emitted untouched, their inner blocks being percentages and
 * descriptors rather than selectors.
 *
 * @param css   the stylesheet to confine
 * @param scope the container selector to confine it to — ARTICLE_SCOPE for the slide-out,
 *              KB_PAGE_SCOPE for the full-page view. Defaults to the slide-out so every existing
 *              caller keeps its behaviour.
 */
export function scopeArticleCss(css: string, scope: string = ARTICLE_SCOPE): string {
  if (!css) return "";
  let out = "";
  let index = 0;

  while (index < css.length) {
    // Comments are copied verbatim; a selector must never be read out of one.
    if (css.startsWith("/*", index)) {
      const stop = skipComment(css, index);
      out += css.slice(index, stop);
      index = stop;
      continue;
    }

    const { at, kind } = nextDelimiter(css, index);

    if (kind === "end") {
      out += css.slice(index);
      break;
    }

    if (kind === "statement") {
      // No block, so this is an at-statement or a stray semicolon — never a selector.
      out += keepStatement(css.slice(index, at + 1));
      index = at + 1;
      continue;
    }

    const prelude = css.slice(index, at);
    const close = matchingBrace(css, at);
    const body = css.slice(at + 1, close);
    const trimmedPrelude = prelude.trim();

    if (trimmedPrelude.startsWith("@")) {
      const name = (trimmedPrelude.match(/^@([a-zA-Z-]+)/) || [])[1] || "";
      const recurse = /^(media|supports|container|layer|scope)$/i.test(name);
      out += prelude + "{" + (recurse ? scopeArticleCss(body, scope) : body) + "}";
    } else {
      const scoped = trimmedPrelude
        .split(",")
        .map((selector) => scopeSelector(selector, scope))
        .filter(Boolean)
        .join(", ");
      // Keep any leading whitespace/newlines so the stylesheet stays readable in devtools.
      const lead = prelude.slice(0, prelude.length - prelude.trimStart().length);
      out += lead + scoped + " {" + body + "}";
    }

    index = close === css.length ? css.length : close + 1;
  }

  return out;
}

/**
 * True when an href would move the browser: anything that is not an in-page anchor or a scheme
 * that hands off to another application.
 *
 * `href=""` counts as navigating. It reloads the current page, which inside the panel means
 * reloading the SPA and losing the reader's chat. One exists in the corpus (wo-employees). Callers
 * must therefore distinguish an EMPTY href from a MISSING one — `<a id="top" name="top">` carries
 * no href attribute at all and is a jump target, not a link.
 */
export function isNavigatingHref(href: string): boolean {
  const trimmed = (href || "").trim();
  if (trimmed.startsWith("#")) return false;
  if (NON_NAVIGATING_SCHEME.test(trimmed)) return false;
  return true;
}

/** Turn one anchor into an inert in-panel dead link. */
function makeDeadLink(anchor: Element): DeadLink {
  const href = anchor.getAttribute("href") || "";
  const info = describeDeadLink(href, anchor.textContent || "");
  anchor.setAttribute(DEAD_LINK_ATTR, info.urlname || info.label);
  anchor.setAttribute("href", "#");
  anchor.removeAttribute("target");
  anchor.removeAttribute("rel");
  anchor.classList.add(DEAD_LINK_CLASS);
  return info;
}

/**
 * Harden every anchor in a parsed article so none of them can navigate out of the panel.
 *
 * Fails CLOSED on purpose. The citation proxy is the only thing that knows the AVSOS hostname —
 * it is kept out of the browser DOM deliberately (avsOsCitation.ts) — so the panel cannot tell an
 * AVSOS article URL from any other absolute URL. An anchor with no proxy marker is therefore an
 * anchor the proxy did not resolve, and it becomes a dead link. If the proxy ever regresses the
 * result is a visible dead page, never an escape.
 */
function hardenAnchors(root: ParentNode): DeadLink[] {
  const neutralised: DeadLink[] = [];

  root.querySelectorAll(LINK_ELEMENTS).forEach((anchor) => {
    // Resolved by the proxy — this is in-panel navigation, leave the marker alone.
    if (anchor.hasAttribute(CITATION_ATTR)) {
      anchor.setAttribute("href", "#");
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
      return;
    }

    // Already marked missing upstream (the AVSOS OS-95 contract). Normalise the presentation.
    if (anchor.hasAttribute(DEAD_LINK_ATTR)) {
      anchor.setAttribute("href", "#");
      anchor.removeAttribute("target");
      anchor.removeAttribute("rel");
      anchor.classList.add(DEAD_LINK_CLASS);
      return;
    }

    /*
     * Explicitly exempted by the server (e.g. Home's marketing CTAs, ACOP-120) — a deliberate,
     * narrow carve-out from "no click leaves Copilot": it opens a new tab rather than navigating
     * the embedding iframe away, so the guarantee still holds. Leave href/target/rel exactly as
     * avsOsArticleHelper.ts set them; the client never re-derives its own allowlist.
     */
    if (anchor.hasAttribute(EXTERNAL_OK_ATTR)) return;

    const href = anchor.getAttribute("href");
    // No href attribute at all: a jump target such as <a id="top" name="top">, not a link.
    if (href === null) return;
    if (!isNavigatingHref(href)) return;

    neutralised.push(makeDeadLink(anchor));
  });

  return neutralised;
}

/** Collect `src` values that arrived relative, so a test can assert there are none. */
function collectRelativeSrcs(root: ParentNode): string[] {
  const relative: string[] = [];
  root.querySelectorAll("[src]").forEach((el) => {
    const src = el.getAttribute("src") || "";
    if (!src) return;
    if (/^(https?:)?\/\//i.test(src) || src.startsWith("data:")) return;
    relative.push(src);
  });
  return relative;
}

/**
 * Sanitise, scope and harden article HTML from the citation proxy.
 *
 * @param rawHtml   the proxy response body
 * @param sanitize  DOMPurify's sanitize, injected so this module stays framework-free and testable
 * @param scope     the container the article's own CSS is confined to. Defaults to the slide-out.
 */
export function prepareArticleHtml(
  rawHtml: string,
  sanitize: Function,
  scope: string = ARTICLE_SCOPE
): PreparedArticle {
  if (!rawHtml || typeof rawHtml !== "string") {
    return { html: "", neutralised: [], relativeSrcs: [] };
  }

  /*
   * The wrapper is load-bearing, not cosmetic. Articles arrive as fragments that OPEN with the
   * vendored <style>; an HTML parser hoists a leading <style> into <head>, and DOMPurify returns
   * body content only, so sanitising the fragment as-is silently drops the entire stylesheet and
   * the article renders unstyled. Wrapping puts the <style> inside <body> where ADD_TAGS keeps it.
   */
  const sanitized = sanitize("<div>" + stripStyleComments(rawHtml) + "</div>", {
    ADD_TAGS,
    ADD_ATTR,
    FORBID_TAGS,
  });

  // The failure this guards against was SILENT — an article just rendered unstyled and nobody was told.
  // If the page carried a stylesheet and none survived sanitising, say so once in the console.
  if (/<style\b/i.test(rawHtml) && !/<style\b/i.test(String(sanitized))) {
    console.warn("[kb] the article's stylesheet was discarded by the sanitiser; it will render unstyled");
  }

  const doc = new DOMParser().parseFromString(
    "<div id=\"c2-article-root\">" + sanitized + "</div>",
    "text/html"
  );
  const root = doc.getElementById("c2-article-root");
  if (!root) return { html: sanitized, neutralised: [], relativeSrcs: [] };

  root.querySelectorAll("style").forEach((style) => {
    style.textContent = scopeArticleCss(style.textContent || "", scope);
  });

  const neutralised = hardenAnchors(root);
  const relativeSrcs = collectRelativeSrcs(root);

  return { html: root.innerHTML, neutralised, relativeSrcs };
}

/**
 * Every anchor still able to navigate away, for use as an assertion in tests and as a last-resort
 * check. A non-empty result means the requirement is broken.
 */
export function findEscapingHrefs(html: string): string[] {
  const doc = new DOMParser().parseFromString(html || "", "text/html");
  const escaping: string[] = [];
  doc.querySelectorAll(LINK_ELEMENTS).forEach((anchor) => {
    // Deliberately, narrowly exempted by the server (EXTERNAL_OK_ATTR) — see hardenAnchors. Not a
    // regression of the guarantee: it's the one shape the guarantee was always meant to allow.
    if (anchor.hasAttribute(EXTERNAL_OK_ATTR)) return;
    // A missing href attribute is not navigation — DOMPurify strips a javascript: href outright,
    // and jump targets like <a id="top"> never had one. An EMPTY href is navigation; see above.
    const href = anchor.getAttribute("href");
    if (href !== null && isNavigatingHref(href)) escaping.push(href);
  });
  return escaping;
}
